/**
 * BESTIX v5.0 — SignInPage
 * Authentication page with login and registration
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthStore, selectIsAuthenticated, selectAuthLoading, selectAuthError } from '@/stores/auth.store';

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth store
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isLoading = useAuthStore(selectAuthLoading);
  const authError = useAuthStore(selectAuthError);
  const login = useAuthStore((state) => state.login);
  const register = useAuthStore((state) => state.register);
  const clearError = useAuthStore((state) => state.clearError);
  
  // Local state
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [localError, setLocalError] = useState<string | null>(null);

  // Get redirect destination from location state or default to home
  const from = (location.state as { from?: string })?.from || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Clear errors when switching modes
  useEffect(() => {
    clearError();
    setLocalError(null);
  }, [isSignUp, clearError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setLocalError(null);
  };

  // Password validation helper
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Validate email and password
    if (!formData.email || !formData.password) {
      setLocalError('Please fill in all required fields.');
      return;
    }

    if (isSignUp) {
      // Validate password complexity for registration
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setLocalError(passwordError);
        return;
      }
    }

    try {
      if (isSignUp) {
        await register({
          email: formData.email,
          password: formData.password,
          name: formData.name.trim() || undefined,
        });
      } else {
        await login(formData.email, formData.password);
      }
      // Navigation happens in useEffect when isAuthenticated changes
    } catch {
      // Error is handled by the store
    }
  };

  const error = localError || authError;

  return (
    <>
      <Helmet><title>{isSignUp ? 'Sign Up' : 'Sign In'} | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <span className="font-playfair text-3xl font-bold text-navy">BESTIX</span>
              </Link>
              <h1 className="text-2xl font-semibold text-navy mt-6">
                {isSignUp ? 'Create your account' : 'Welcome back'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isSignUp ? 'Join millions of fans' : 'Sign in to your account'}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {isSignUp && (
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="John Doe"
                      disabled={isLoading}
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    placeholder="you@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password *
                    </label>
                    {!isSignUp && (
                      <button type="button" className="text-sm text-gold hover:underline">
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    placeholder="••••••••"
                    required
                    minLength={8}
                    disabled={isLoading}
                  />
                  {isSignUp && (
                    <p className="text-xs text-gray-500 mt-1">
                      Must be at least 8 characters
                    </p>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="w-full btn btn-primary py-3 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {isSignUp ? 'Creating Account...' : 'Signing In...'}
                    </span>
                  ) : (
                    isSignUp ? 'Create Account' : 'Sign In'
                  )}
                </button>
              </form>

              {/* Toggle */}
              <p className="text-center text-sm text-gray-600 mt-6">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-navy font-semibold hover:text-gold"
                  disabled={isLoading}
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>

            <p className="text-center text-xs text-gray-500 mt-6">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="underline">Terms</Link> and{' '}
              <Link to="/privacy" className="underline">Privacy Policy</Link>
            </p>
          </div>
        </div>

        {/* Right side - Image/Branding (hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-navy items-center justify-center p-12">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <svg className="w-24 h-24 mx-auto text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <h2 className="font-playfair text-3xl font-bold text-white mb-4">
              Your Ticket to Unforgettable Moments
            </h2>
            <p className="text-gray-300">
              Access the world's most sought-after events with confidence. Every ticket backed by our 100% Buyer Guarantee.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
